import { type PointerEvent, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { CheckCircle2, FileSignature, Loader2, ScrollText, XCircle } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { formatCurrency, formatDateTime } from "@/lib/i18n";

interface PublicProposalSection {
  id: string;
  title: string;
  sectionType: string;
  content: {
    blocks?: Array<{
      text?: string;
    }>;
  };
}

interface PublicProposalResponse {
  data: {
    proposalId: string;
    title: string;
    currency: string;
    totalValue: number | null;
    validUntil: string | null;
    status: string;
    signerName: string;
    sections: PublicProposalSection[];
  };
}

function formatMoney(amount: number | null, currency: string): string {
  if (amount === null) {
    return "Value pending";
  }

  return formatCurrency(amount, currency);
}

function extractParagraphs(section: PublicProposalSection): string[] {
  const blocks = section.content?.blocks;
  if (!Array.isArray(blocks)) {
    return [];
  }

  return blocks
    .map((block) => {
      if (!block || typeof block !== "object") {
        return null;
      }

      const text = block.text;
      if (typeof text !== "string") {
        return null;
      }

      const trimmed = text.trim();
      return trimmed || null;
    })
    .filter((value): value is string => value !== null);
}

export default function PublicProposalSignPage() {
  const { tenantSlug, proposalId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const startedAtRef = useRef<number>(Date.now());
  const hasTrackedEngagementRef = useRef(false);
  const maxScrollDepthRef = useRef(0);
  const [detail, setDetail] = useState<PublicProposalResponse["data"] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [decisionState, setDecisionState] = useState<"idle" | "signing" | "declining" | "signed" | "declined">("idle");
  const [signerName, setSignerName] = useState("");
  const [signatureText, setSignatureText] = useState("");
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [signatureMode, setSignatureMode] = useState<"typed" | "drawn">("drawn");
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);

  useEffect(() => {
    if (!tenantSlug || !proposalId || !token) {
      setError("The proposal signing link is incomplete.");
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const response = await apiFetch<PublicProposalResponse>(
          `/api/public/proposals/sign/${tenantSlug}/${proposalId}?token=${encodeURIComponent(token)}`,
        );
        if (cancelled) {
          return;
        }

        setDetail(response.data);
        setSignerName(response.data.signerName);
        setSignatureText(response.data.signerName);
        startedAtRef.current = Date.now();
        hasTrackedEngagementRef.current = false;
        setError(null);
      } catch (loadError) {
        if (cancelled) {
          return;
        }

        setError(loadError instanceof Error ? loadError.message : "Failed to load proposal signing page");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [proposalId, tenantSlug, token]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const updateScrollDepth = () => {
      const doc = document.documentElement;
      const scrollHeight = Math.max(doc.scrollHeight, window.innerHeight);
      if (scrollHeight <= 0) {
        return;
      }
      const currentDepth = Math.min(
        100,
        Math.round(((window.scrollY + window.innerHeight) / scrollHeight) * 100),
      );
      maxScrollDepthRef.current = Math.max(maxScrollDepthRef.current, currentDepth);
    };

    const sendEngagement = () => {
      if (!tenantSlug || !proposalId || !token || !detail || hasTrackedEngagementRef.current) {
        return;
      }

      hasTrackedEngagementRef.current = true;
      const dwellTimeSec = Math.max(0, Math.round((Date.now() - startedAtRef.current) / 1000));
      const scrollDepthPercent = Math.max(maxScrollDepthRef.current, 1);

      void fetch(`/api/public/proposals/sign/${tenantSlug}/${proposalId}/track`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          dwellTimeSec,
          scrollDepthPercent,
        }),
        keepalive: true,
      }).catch(() => undefined);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        updateScrollDepth();
        sendEngagement();
      }
    };

    updateScrollDepth();
    window.addEventListener("scroll", updateScrollDepth, { passive: true });
    window.addEventListener("pagehide", sendEngagement);
    window.addEventListener("beforeunload", sendEngagement);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("scroll", updateScrollDepth);
      window.removeEventListener("pagehide", sendEngagement);
      window.removeEventListener("beforeunload", sendEngagement);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [detail, proposalId, tenantSlug, token]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);

    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    context.scale(ratio, ratio);
    context.lineWidth = 2.4;
    context.lineCap = "round";
    context.lineJoin = "round";
    context.strokeStyle = "#0f172a";
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
  }, []);

  function getCanvasContext() {
    const canvas = canvasRef.current;
    if (!canvas) {
      return null;
    }

    return canvas.getContext("2d");
  }

  function getCanvasPoint(event: PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) {
      return null;
    }

    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  function startDrawing(event: PointerEvent<HTMLCanvasElement>) {
    const context = getCanvasContext();
    const point = getCanvasPoint(event);
    if (!context || !point) {
      return;
    }

    isDrawingRef.current = true;
    context.beginPath();
    context.moveTo(point.x, point.y);
    setHasDrawnSignature(true);
  }

  function continueDrawing(event: PointerEvent<HTMLCanvasElement>) {
    if (!isDrawingRef.current) {
      return;
    }

    const context = getCanvasContext();
    const point = getCanvasPoint(event);
    if (!context || !point) {
      return;
    }

    context.lineTo(point.x, point.y);
    context.stroke();
  }

  function stopDrawing() {
    const context = getCanvasContext();
    if (!context) {
      return;
    }

    if (isDrawingRef.current) {
      context.closePath();
    }
    isDrawingRef.current = false;
  }

  function clearCanvas() {
    const canvas = canvasRef.current;
    const context = getCanvasContext();
    if (!canvas || !context) {
      return;
    }

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight);
    context.beginPath();
    setHasDrawnSignature(false);
  }

  async function signProposal() {
    if (!tenantSlug || !proposalId || !token) {
      setError("The proposal signing link is incomplete.");
      return;
    }

    if (!legalAccepted) {
      setError("You must accept the electronic signature statement before signing.");
      return;
    }

    if (signatureMode === "typed" && signatureText.trim().length < 2) {
      setError("Enter your typed signature before signing.");
      return;
    }

    if (signatureMode === "drawn" && !hasDrawnSignature) {
      setError("Draw your signature before signing.");
      return;
    }

    setDecisionState("signing");
    setError(null);
    const drawnSignature = canvasRef.current?.toDataURL("image/png") ?? "";
    try {
      await apiFetch(`/api/public/proposals/sign/${tenantSlug}/${proposalId}`, {
        method: "POST",
        body: JSON.stringify({
          token,
          legalAccepted: true,
          signatureMethod: signatureMode,
          signatureData: signatureMode === "typed" ? signatureText.trim() : drawnSignature,
          signerName: signerName.trim() || undefined,
        }),
      });
      setDecisionState("signed");
    } catch (signError) {
      setDecisionState("idle");
      setError(signError instanceof Error ? signError.message : "Failed to sign proposal");
    }
  }

  async function declineProposal() {
    if (!tenantSlug || !proposalId || !token) {
      setError("The proposal signing link is incomplete.");
      return;
    }

    setDecisionState("declining");
    setError(null);
    try {
      await apiFetch(`/api/public/proposals/sign/${tenantSlug}/${proposalId}/decline`, {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      setDecisionState("declined");
    } catch (declineError) {
      setDecisionState("idle");
      setError(declineError instanceof Error ? declineError.message : "Failed to decline proposal");
    }
  }

  if (error && !detail) {
    return <main className="mx-auto max-w-3xl px-6 py-16 text-sm text-[var(--color-destructive)]">{error}</main>;
  }

  if (!detail) {
    return (
      <main className="mx-auto flex min-h-screen max-w-3xl items-center justify-center px-6 py-16 text-sm text-[var(--color-muted-foreground)]">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading proposal…
      </main>
    );
  }

  if (decisionState === "signed") {
    return (
      <main className="mx-auto flex min-h-screen max-w-3xl items-center px-6 py-16">
        <div className="w-full rounded-3xl border border-[var(--color-success)]/30 bg-[var(--color-success)]/10 p-8 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-[var(--color-success)]" />
          <h1 className="mt-4 text-2xl font-semibold text-[var(--color-foreground)]">Proposal signed</h1>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            Your electronic signature was recorded and the signed document package was generated for the sender.
          </p>
        </div>
      </main>
    );
  }

  if (decisionState === "declined") {
    return (
      <main className="mx-auto flex min-h-screen max-w-3xl items-center px-6 py-16">
        <div className="w-full rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-8 text-center">
          <XCircle className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)]" />
          <h1 className="mt-4 text-2xl font-semibold text-[var(--color-foreground)]">Proposal declined</h1>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            The sender can revise the offer and issue a new signing request if they want to continue.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(20,184,166,0.12),_transparent_34%),linear-gradient(180deg,#f7fbff_0%,#ecfeff_100%)] px-4 py-10 sm:px-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <section className="rounded-3xl border border-[var(--color-border)] bg-white/90 p-8 shadow-sm backdrop-blur">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-3">
              <div className="text-xs font-medium uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
                Proposal signature review
              </div>
              <h1 className="text-3xl font-semibold text-[var(--color-foreground)]">{detail.title}</h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-[var(--color-muted-foreground)]">
                <span className="rounded-full bg-[var(--color-primary)]/8 px-3 py-1 font-medium text-[var(--color-primary)]">
                  {formatMoney(detail.totalValue, detail.currency)}
                </span>
                <span>Status: {detail.status}</span>
                {detail.validUntil ? <span>Valid until {formatDateTime(detail.validUntil)}</span> : null}
              </div>
            </div>

            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-muted-foreground)]">
              <div className="font-medium text-[var(--color-foreground)]">Signer</div>
              <div className="mt-1">{detail.signerName}</div>
            </div>
          </div>
        </section>

        <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
          <section className="space-y-4 rounded-3xl border border-[var(--color-border)] bg-white/90 p-6 shadow-sm backdrop-blur">
            <div className="flex items-center gap-2">
              <ScrollText className="h-5 w-5 text-[var(--color-primary)]" />
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Proposal sections</h2>
            </div>

            <div className="space-y-4">
              {detail.sections.map((section) => {
                const paragraphs = extractParagraphs(section);
                return (
                  <section
                    key={section.id}
                    className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5"
                  >
                    <div className="mb-2">
                      <h3 className="text-base font-semibold text-[var(--color-foreground)]">{section.title}</h3>
                      <p className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">{section.sectionType}</p>
                    </div>
                    <div className="space-y-3 text-sm leading-6 text-[var(--color-muted-foreground)]">
                      {paragraphs.length > 0 ? (
                        paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)
                      ) : (
                        <p>No structured content blocks were returned for this section.</p>
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          </section>

          <aside className="space-y-4 rounded-3xl border border-[var(--color-border)] bg-white/95 p-6 shadow-sm backdrop-blur">
            <div className="flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-[var(--color-primary)]" />
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Sign electronically</h2>
            </div>

            <p className="text-sm leading-6 text-[var(--color-muted-foreground)]">
              Type your legal name below to sign this proposal. The platform records the signed document, evidence package,
              signing timestamp, and request metadata.
            </p>

            <label className="block space-y-2">
              <span className="text-sm font-medium text-[var(--color-foreground)]">Signer name</span>
              <input
                value={signerName}
                onChange={(event) => setSignerName(event.target.value)}
                className="w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)] outline-none transition focus:border-[var(--color-primary)]"
                placeholder="Alex Example"
              />
            </label>

            <label className="block space-y-2">
              <span className="text-sm font-medium text-[var(--color-foreground)]">Signature method</span>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setSignatureMode("drawn")}
                  className={`rounded-2xl border px-4 py-3 text-sm font-medium transition ${
                    signatureMode === "drawn"
                      ? "border-[var(--color-primary)] bg-[var(--color-primary)]/8 text-[var(--color-primary)]"
                      : "border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)]"
                  }`}
                >
                  Draw signature
                </button>
                <button
                  type="button"
                  onClick={() => setSignatureMode("typed")}
                  className={`rounded-2xl border px-4 py-3 text-sm font-medium transition ${
                    signatureMode === "typed"
                      ? "border-[var(--color-primary)] bg-[var(--color-primary)]/8 text-[var(--color-primary)]"
                      : "border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)]"
                  }`}
                >
                  Type signature
                </button>
              </div>
            </label>

            {signatureMode === "drawn" ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-[var(--color-foreground)]">Draw signature</span>
                  <button
                    type="button"
                    onClick={clearCanvas}
                    className="text-sm text-[var(--color-primary)] underline-offset-4 hover:underline"
                  >
                    Clear
                  </button>
                </div>
                <canvas
                  ref={canvasRef}
                  data-testid="proposal-signature-canvas"
                  className="h-44 w-full touch-none rounded-2xl border border-dashed border-[var(--color-border)] bg-white"
                  onPointerDown={startDrawing}
                  onPointerMove={continueDrawing}
                  onPointerUp={stopDrawing}
                  onPointerLeave={stopDrawing}
                />
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  Sign inside the canvas. The drawn signature is stored with the evidence package.
                </p>
              </div>
            ) : (
              <label className="block space-y-2">
                <span className="text-sm font-medium text-[var(--color-foreground)]">Typed signature</span>
                <input
                  value={signatureText}
                  onChange={(event) => setSignatureText(event.target.value)}
                  className="w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)] outline-none transition focus:border-[var(--color-primary)]"
                  placeholder="Type your full legal name"
                />
              </label>
            )}

            <label className="flex items-start gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 text-sm text-[var(--color-muted-foreground)]">
              <input
                type="checkbox"
                checked={legalAccepted}
                onChange={(event) => setLegalAccepted(event.target.checked)}
                className="mt-1 h-4 w-4 rounded border-[var(--color-border)]"
              />
              <span>
                I agree to electronically sign and accept this proposal, and I understand that my signature will be stored
                with the signed document and audit evidence.
              </span>
            </label>

            {error ? (
              <div className="rounded-2xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
                {error}
              </div>
            ) : null}

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={signProposal}
                disabled={decisionState === "signing"}
                className="inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium text-white disabled:opacity-60"
                style={{ background: "var(--gradient-energy)" }}
              >
                {decisionState === "signing" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />}
                Sign proposal
              </button>

              <button
                type="button"
                onClick={declineProposal}
                disabled={decisionState === "declining" || decisionState === "signing"}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[var(--color-border)] px-4 py-3 text-sm font-medium text-[var(--color-foreground)] disabled:opacity-60"
              >
                {decisionState === "declining" ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                Decline proposal
              </button>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
